/**
 * Variable analytics privacy selectors.
 *
 * Production variable editors and tests import the same registry so selector
 * values cannot drift between UI and automation.
 */

export function variablePrivacyDecisionTestIds(prefix: string) {
  return {
    group: `${prefix}-analytics-privacy-group`,
    collect: `${prefix}-analytics-privacy-collect`,
    exclude: `${prefix}-analytics-privacy-exclude`,
    reviewRequired: `${prefix}-analytics-privacy-review-required`,
  } as const;
}

export const PRODUCT_VARIABLES = {
  accordion: 'product-variables-accordion',
  editor: 'product-variable-set-editor',
  addButton: 'product-variable-add-button',
  dialog: 'product-variable-dialog',
  saveButton: 'product-variable-save-button',
  nameInput: 'product-variable-name-input',
  decisionPrefix: 'product-variable',
  row: (variableId: string) => `product-variable-row-${variableId}`,
  status: (variableId: string) => `product-variable-privacy-status-${variableId}`,
  /** UXR C6: inline action on a Review-required row — opens the variable editor. */
  resolve: (variableId: string) => `product-variable-resolve-${variableId}`,
  /** Bulk selection toolbar (variables-bulk-actions). */
  selectAll: 'product-variable-select-all',
  selectionCount: 'product-variable-selection-count',
  bulkAnalyticsMenu: 'product-variable-bulk-analytics-menu',
  bulkAnalyticsCollect: 'product-variable-bulk-analytics-collect',
  bulkAnalyticsExclude: 'product-variable-bulk-analytics-exclude',
  bulkRequiredMenu: 'product-variable-bulk-required-menu',
  bulkRequiredYes: 'product-variable-bulk-required-yes',
  bulkRequiredNo: 'product-variable-bulk-required-no',
  bulkDelete: 'product-variable-bulk-delete',
  clearSelection: 'product-variable-clear-selection',
} as const;

export const V2_VARIABLES = {
  panel: 'v2-variable-crud-panel',
  addButton: 'v2-variable-add-button',
  createForm: 'v2-variable-create-form',
  createNameInput: 'v2-variable-create-name-input',
  createDescriptionInput: 'v2-variable-create-description-input',
  createButton: 'v2-variable-create-button',
  createDecisionPrefix: 'v2-variable-create',
  editDialog: 'v2-variable-edit-dialog',
  editNameInput: 'v2-variable-edit-name-input',
  editDescriptionInput: 'v2-variable-edit-description-input',
  editSaveButton: 'v2-variable-edit-save-button',
  editDecisionPrefix: 'v2-variable-edit',
  row: (variableId: string) => `v2-variable-row-${variableId}`,
  editButton: (variableId: string) => `v2-variable-edit-button-${variableId}`,
  deleteButton: (variableId: string) => `v2-variable-delete-button-${variableId}`,
  status: (variableId: string) => `v2-variable-privacy-status-${variableId}`,
  privacyMenuCollect: 'v2-variable-privacy-menu-collect',
  privacyMenuExclude: 'v2-variable-privacy-menu-exclude',
  /** Bulk selection (variables-bulk-actions). */
  selectAll: 'v2-variable-select-all',
  rowCheckbox: (variableId: string) => `v2-variable-row-checkbox-${variableId}`,
  selectionCount: 'v2-variable-selection-count',
  bulkAnalyticsMenu: 'v2-variable-bulk-analytics-menu',
  bulkAnalyticsCollect: 'v2-variable-bulk-analytics-collect',
  bulkAnalyticsExclude: 'v2-variable-bulk-analytics-exclude',
  bulkDelete: 'v2-variable-bulk-delete',
  bulkDeleteConfirmDialog: 'v2-variable-bulk-delete-confirm-dialog',
  bulkDeleteConfirmButton: 'v2-variable-bulk-delete-confirm-button',
  clearSelection: 'v2-variable-clear-selection',
} as const;

/** Publish-dialog privacy review surface (DeployWebPlacementDialog). */
export const PUBLISH_PRIVACY_REVIEW = {
  container: 'webpl-publish-privacy-review',
  row: (variableId: string) => `webpl-publish-privacy-row-${variableId}`,
  collect: (variableId: string) => `webpl-publish-privacy-collect-${variableId}`,
  exclude: (variableId: string) => `webpl-publish-privacy-exclude-${variableId}`,
  saveButton: 'webpl-publish-privacy-save',
  /** A/B-only escape hatch — the server does not gate A/B routing publishes. */
  skipButton: 'webpl-publish-privacy-skip',
  /** Bulk review bar (variables-bulk-actions); rowCheckbox keyed like row(). */
  selectAll: 'webpl-publish-privacy-select-all',
  rowCheckbox: (variableId: string) => `webpl-publish-privacy-row-checkbox-${variableId}`,
  selectionCount: 'webpl-publish-privacy-selection-count',
  bulkCollect: 'webpl-publish-privacy-bulk-collect',
  bulkExclude: 'webpl-publish-privacy-bulk-exclude',
} as const;
